"use client"

import { useState, useRef } from "react"
import { Loader2, Send, CheckCircle, XCircle } from "lucide-react"

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null
    message: string
  }>({ type: null, message: "" })
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: "" })

    const formData = new FormData(event.currentTarget)

    // Using URLSearchParams for maximum compatibility with PHP $_POST
    const params = new URLSearchParams()
    params.append("fname", formData.get("fname") as string)
    params.append("lname", formData.get("lname") as string)
    params.append("email", formData.get("email") as string)
    params.append("phno", formData.get("phno") as string)
    params.append("subject", formData.get("subject") as string)
    params.append("msg", formData.get("msg") as string)

    console.log("📤 Submitting contact form (URLSearchParams):", Object.fromEntries(params.entries()))

    try {
      const response = await fetch("https://dextro-online.com/lokbhartieducation.in/API/post_contactus.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      })

      console.log("📥 Response status:", response.status)
      const responseText = await response.text()
      console.log("📥 Response text:", responseText)

      let result
      try {
        result = JSON.parse(responseText)
      } catch (e) {
        // Return generic success if OK but parsing failed (common with some PHP APIs)
        if (response.ok && (responseText.toLowerCase().includes("success") || responseText.toLowerCase().includes("submitted") || responseText.toLowerCase().includes("1"))) {
          result = { success: true, message: "Thank you for contacting us! We will get back to you soon." }
        } else {
          throw new Error(responseText || "Server Error (500)")
        }
      }

      if (result.success || result.status === "success" || result.status === "1") {
        setSubmitStatus({
          type: "success",
          message: result.message || "Thank you for contacting us! We will get back to you soon.",
        })
        if (formRef.current) {
          formRef.current.reset()
        }
      } else {
        throw new Error(result.message || "Failed to submit form. Please try again.")
      }
    } catch (error: any) {
      console.error("❌ Submission error:", error)
      setSubmitStatus({
        type: "error",
        message: error.message || "Unable to submit form. Please try again later.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="rounded-[32px] border border-blue-100/70 bg-white/95 p-6 shadow-[0px_22px_58px_rgba(59,130,246,0.12)] sm:p-8"
    >
      <div className="mb-6">
        <h3 className="text-2xl font-serif font-semibold text-[#0b2157]">Send us a Message</h3>
        <p className="mt-2 text-sm text-[#5160a6]">
          Fill out the form below and our team will respond shortly.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#1e3a8a]">
            First Name<span className="text-blue-500"> *</span>
          </label>
          <input
            required
            type="text"
            name="fname"
            placeholder="Your first name"
            className="rounded-xl border border-blue-100 bg-[#f0f9ff] px-4 py-3 text-sm text-[#1e293b] outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#1e3a8a]">
            Last Name<span className="text-blue-500"> *</span>
          </label>
          <input
            required
            type="text"
            name="lname"
            placeholder="Your last name"
            className="rounded-xl border border-blue-100 bg-[#f0f9ff] px-4 py-3 text-sm text-[#1e293b] outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#1e3a8a]">
            Email<span className="text-blue-500"> *</span>
          </label>
          <input
            required
            type="email"
            name="email"
            placeholder="your@email.com"
            className="rounded-xl border border-blue-100 bg-[#f0f9ff] px-4 py-3 text-sm text-[#1e293b] outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#1e3a8a]">
            Phone No.<span className="text-blue-500"> *</span>
          </label>
          <input
            required
            type="tel"
            name="phno"
            placeholder="Your phone number"
            className="rounded-xl border border-blue-100 bg-[#f0f9ff] px-4 py-3 text-sm text-[#1e293b] outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label className="text-sm font-semibold text-[#1e3a8a]">Subject</label>
        <input
          type="text"
          name="subject"
          placeholder="What's this about?"
          className="rounded-xl border border-blue-100 bg-[#f0f9ff] px-4 py-3 text-sm text-[#1e293b] outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
        />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label className="text-sm font-semibold text-[#1e3a8a]">
          Message<span className="text-blue-500"> *</span>
        </label>
        <textarea
          required
          name="msg"
          rows={5}
          placeholder="Tell us about your inquiry..."
          className="rounded-xl border border-blue-100 bg-[#f0f9ff] px-4 py-3 text-sm text-[#1e293b] outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {submitStatus.type && (
        <div
          className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${submitStatus.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
            }`}
        >
          {submitStatus.type === "success" ? (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <XCircle className="h-5 w-5 flex-shrink-0" />
          )}
          <p>{submitStatus.message}</p>
        </div>
      )}

      <button
        type="submit"
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-[0px_18px_38px_rgba(30,58,138,0.35)] transition hover:shadow-[0px_20px_42px_rgba(30,58,138,0.38)] focus:outline-none focus:ring-2 focus:ring-blue-200"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            Send Message
          </>
        )}
      </button>

      {/* Debug Info */}

    </form>
  )
}